import { createElement } from 'react';
import type {
  StorageRecoverability,
  StorageResourceRef,
} from '../../../../types/azureStorage';
import { useTransfersStore } from '../../../transfers/store/transfersStore';
import { toast } from '../../../../components/ui/toaster';
import { openConfirmDialog } from '../../common/components/confirm/confirmDialogStore';
import {
  getAzureStorageApi,
  newStorageRequestId,
  unwrapStorageResult,
} from '../services/azureStorageApi';
import { trackStorageEvent } from '../services/storageAnalytics';
import { buildStorageDeleteConfirmation } from './storageDeleteConfirm';
import { DeleteConfirmationView } from './DeleteConfirmationView';

const REQUEST_TIMEOUT_MS = 120_000;
/** Recoverability gates the delete confirmation; a hung properties read must not hold the dialog back. */
const RECOVERABILITY_TIMEOUT_MS = 15_000;

export type StorageMutationSource = 'toolbar' | 'row' | 'keyboard' | 'drop' | 'tree';
export type StorageJobOutcome = { kind: 'started'; jobId: string } | { kind: 'dialog-cancelled' };

function requireApi() {
  const api = getAzureStorageApi();
  if (!api) throw new Error('Azure Storage is not available in this build.');
  return api;
}

function noteJob(jobId: string, source: StorageMutationSource): void {
  // Main's job snapshot remains the analytics truth for UI/MCP origin; this renderer hint records the
  // surface that started a direct user job (review C N3), distinct from restored / MCP jobs.
  useTransfersStore.getState().noteJobSource(jobId, source);
}

export async function requestStorageUpload(args: {
  connectionId: string;
  destination: Extract<StorageResourceRef, { kind: 'container' | 'blobPrefix' }>;
  sources?: string[];
  sourceKind?: 'files' | 'folder';
  collision?: 'ask' | 'skip' | 'overwrite' | 'rename';
  source: StorageMutationSource;
}): Promise<StorageJobOutcome> {
  const value = unwrapStorageResult(await requireApi().upload({
    connectionId: args.connectionId,
    destination: args.destination,
    requestId: newStorageRequestId(),
    timeoutMs: REQUEST_TIMEOUT_MS,
    ...(args.sources ? { sources: args.sources } : {}),
    ...(args.sourceKind ? { sourceKind: args.sourceKind } : {}),
    ...(args.collision ? { collision: args.collision } : {}),
  }));
  if (!value.started) return { kind: 'dialog-cancelled' };
  noteJob(value.jobId, args.source);
  // Review E F15: one rule for side-effect actions — the event fires on a CONFIRMED start (like feature:storage_delete),
  // never for a cancelled picker or a refused request. `kind`: the picker kind, or the drop's item count bucket.
  trackStorageEvent('feature:storage_upload', { source: args.source, kind: args.sourceKind ?? ((args.sources?.length ?? 0) > 1 ? 'multiple' : 'single') });
  return { kind: 'started', jobId: value.jobId };
}

export async function deleteStorageResources(args: {
  connectionId: string;
  resources: StorageResourceRef[];
  source: StorageMutationSource;
}): Promise<StorageJobOutcome> {
  const value = unwrapStorageResult(await requireApi().delete({
    connectionId: args.connectionId,
    resources: args.resources,
    requestId: newStorageRequestId(),
    timeoutMs: REQUEST_TIMEOUT_MS,
  }));
  if (!value.started) return { kind: 'dialog-cancelled' };
  noteJob(value.jobId, args.source);
  return { kind: 'started', jobId: value.jobId };
}

export async function createStorageContainer(args: {
  connectionId: string;
  name: string;
  publicAccess: 'container' | 'blob' | 'none';
  /** Supplied by a caller that owns the request's lifetime (the dialog cancels it on close, review C C11). */
  requestId?: string;
}): Promise<void> {
  const { requestId, ...request } = args;
  unwrapStorageResult(await requireApi().createContainer({ ...request, requestId: requestId ?? newStorageRequestId(), timeoutMs: REQUEST_TIMEOUT_MS }));
}

export async function deleteStorageContainer(connectionId: string, container: string): Promise<void> {
  unwrapStorageResult(await requireApi().deleteContainer({ connectionId, container, requestId: newStorageRequestId(), timeoutMs: REQUEST_TIMEOUT_MS }));
}

export async function readStorageRecoverability(connectionId: string): Promise<StorageRecoverability> {
  return unwrapStorageResult(await requireApi().recoverability({ connectionId, requestId: newStorageRequestId(), timeoutMs: RECOVERABILITY_TIMEOUT_MS }));
}

const UNKNOWN_RECOVERABILITY: StorageRecoverability = {
  blobSoftDelete: { state: 'unknown', reason: 'Service properties could not be read.' },
  containerSoftDelete: { state: 'unknown', reason: 'Not exposed by this adapter.' },
  versioning: 'unknown',
};

export async function confirmStorageDelete(args: {
  connectionId: string;
  resources: StorageResourceRef[];
  source: StorageMutationSource;
  refresh: () => void;
}): Promise<void> {
  if (args.resources.length === 0) return;
  let recoverability = UNKNOWN_RECOVERABILITY;
  try { recoverability = await readStorageRecoverability(args.connectionId); } catch { /* confirmation remains conservative */ }
  const copy = buildStorageDeleteConfirmation(args.resources, recoverability);
  openConfirmDialog({
    ...copy,
    // UI/UX fix round A (U15): the structured body (where, the items, Recovery, irreversibility); typed-name deletes keep their sentence.
    description: copy.body ? createElement(DeleteConfirmationView, { body: copy.body, testId: 'storage-delete-confirmation' }) : copy.description,
    variant: 'destructive',
    onConfirm: async () => {
      try {
        const container = args.resources.length === 1 && args.resources[0].kind === 'container' ? args.resources[0].container : undefined;
        if (container) await deleteStorageContainer(args.connectionId, container);
        else await deleteStorageResources({ connectionId: args.connectionId, resources: args.resources, source: args.source });
        void window.electronAPI?.analytics?.track('feature:storage_delete', { source: args.source, kind: container ? 'container' : 'items' }).catch(() => undefined);
        args.refresh();
      } catch (error) {
        const message = error && typeof error === 'object' && 'message' in error ? String(error.message) : 'Delete could not be started.';
        toast.error({ title: 'Delete failed', description: message });
        args.refresh();
        throw error;
      }
    },
  });
}
